import React, { useState, useEffect } from 'react';
import { XMarkIcon, UserIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { CalendarEvent, CalendarEventCreate, CalendarEventUpdate } from '../../api/calendar';
import { useFamilyStore } from '../../store/familyStore';

interface EventFormProps {
  event?: CalendarEvent | null;
  initialDate?: Date;
  onClose: () => void;
  onSubmit: (data: CalendarEventCreate | CalendarEventUpdate) => Promise<void>;
}

const DEFAULT_COLORS = [
  '#6366f1', // indigo
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#ef4444', // red
  '#f59e0b', // amber
  '#10b981', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
];

export default function EventForm({ event, initialDate, onClose, onSubmit }: EventFormProps) {
  const { currentFamily } = useFamilyStore();
  const [name, setName] = useState(event?.name || '');
  const [description, setDescription] = useState(event?.description || '');
  const [location, setLocation] = useState(event?.location || '');
  const [startDate, setStartDate] = useState(
    event?.start_datetime
      ? format(new Date(event.start_datetime), 'yyyy-MM-dd')
      : initialDate
      ? format(initialDate, 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd')
  );
  const [startTime, setStartTime] = useState(
    event?.start_datetime && !event.all_day
      ? format(new Date(event.start_datetime), 'HH:mm')
      : '09:00'
  );
  const [endDate, setEndDate] = useState(
    event?.end_datetime
      ? format(new Date(event.end_datetime), 'yyyy-MM-dd')
      : initialDate
      ? format(initialDate, 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd')
  );
  const [endTime, setEndTime] = useState(
    event?.end_datetime && !event.all_day
      ? format(new Date(event.end_datetime), 'HH:mm')
      : '10:00'
  );
  const [allDay, setAllDay] = useState(event?.all_day || false);
  const [color, setColor] = useState(event?.color || DEFAULT_COLORS[0]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(
    event?.members?.map(m => m.id) || []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!name.trim()) {
      setError('Event name is required');
      return;
    }

    const startDatetime = allDay
      ? new Date(`${startDate}T00:00:00`)
      : new Date(`${startDate}T${startTime}:00`);
    const endDatetime = allDay
      ? new Date(`${endDate}T23:59:59`)
      : new Date(`${endDate}T${endTime}:00`);

    if (endDatetime < startDatetime) {
      setError('End date/time must be after start date/time');
      return;
    }

    setIsSubmitting(true);

    try {
      const data: CalendarEventCreate | CalendarEventUpdate = {
        name: name.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        start_datetime: startDatetime.toISOString(),
        end_datetime: endDatetime.toISOString(),
        all_day: allDay,
        color,
        member_ids: selectedMemberIds,
      };

      await onSubmit(data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save event');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface-900 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-700">
          <h2 className="text-2xl font-bold text-white">
            {event ? 'Edit Event' : 'Create Event'}
          </h2>
          <button
            onClick={onClose}
            className="text-surface-400 hover:text-white transition-colors"
            disabled={isSubmitting}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Event Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter event name"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              rows={4}
              placeholder="Enter event description"
              disabled={isSubmitting}
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Location
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter location"
              disabled={isSubmitting}
            />
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="allDay"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="w-4 h-4 text-primary-600 bg-surface-800 border-surface-700 rounded focus:ring-primary-500"
              disabled={isSubmitting}
            />
            <label htmlFor="allDay" className="text-sm font-medium text-surface-300">
              All Day Event
            </label>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
                disabled={isSubmitting}
              />
            </div>
            {!allDay && (
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Start Time *
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={isSubmitting}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                End Date *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
                disabled={isSubmitting}
              />
            </div>
            {!allDay && (
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  End Time *
                </label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={isSubmitting}
                />
              </div>
            )}
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">
              Color
            </label>
            <div className="flex gap-2 flex-wrap">
              {DEFAULT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-10 h-10 rounded-lg border-2 transition-all ${
                    color === c
                      ? 'border-white scale-110'
                      : 'border-surface-700 hover:border-surface-600'
                  }`}
                  style={{ backgroundColor: c }}
                  disabled={isSubmitting}
                />
              ))}
            </div>
          </div>

          {/* Members */}
          {currentFamily && currentFamily.members && currentFamily.members.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                <UserIcon className="w-4 h-4 inline mr-1" />
                Assign to Members (Optional)
              </label>
              <div className="max-h-48 overflow-y-auto border border-surface-700 rounded-lg p-3 space-y-2">
                {currentFamily.members
                  .filter(m => m.is_active)
                  .map((member) => {
                    const isSelected = selectedMemberIds.includes(member.id);
                    const displayName = member.nickname || member.first_name || member.username || 'Unknown';
                    const avatarColor = member.color || '#6366f1';
                    
                    return (
                      <label
                        key={member.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-800/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMemberIds([...selectedMemberIds, member.id]);
                            } else {
                              setSelectedMemberIds(selectedMemberIds.filter(id => id !== member.id));
                            }
                          }}
                          className="w-4 h-4 text-primary-600 bg-surface-800 border-surface-700 rounded focus:ring-primary-500"
                          disabled={isSubmitting}
                        />
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ backgroundColor: avatarColor }}
                        >
                          {displayName.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white text-sm flex-1">
                          {displayName}
                          {member.last_name && ` ${member.last_name}`}
                        </span>
                      </label>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-surface-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-surface-400 hover:text-white transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : event ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

